import { SolarRgbImageService } from '../../../_services/solar.rgb.image.service';
import Vue from 'vue';
import { recordSyncIntent, SyncReason, nextPipelineCorrelationId } from '@/map/reportDefectPipeline';
import { processImageUrlForCdn } from '@/utils/imagePolygonManager/imageLoader';
import { fetchChildDefectTypes, fetchParentDefectTypes } from '@/services/defect/defectType.repository';
import { findCellParentType, getDefectCodeType } from '@/services/defect/defectCode.utils';
import { loadDefectDialogSession } from '@/services/defect/defectDialogSession.loader';
import { resolveDefectShapeGeomForOverlay } from '@/utils/defect/defectShapeOverlay';
import { resolveThermalImageForDefectDialog, syncDashboardSelectedThermalImage } from '@/features/defect/resolveThermalImageForDefectDialog.js';
import { coveringIndexesForZoneFilter, resolveImageNavStep } from '@/features/defect-drawer/utils/resolveModuleCoveringIrImages';
import { createDefaultDefectShapes } from '@/utils/defect/defaultDefectDrawerRect';
import { shouldSkipFullDefectRefresh } from '@/features/defect/optimisticModuleListCreate';

/** 현재 작업이 끝난 뒤 인접 RGB·IR 프리패치를 돌려 메인 로드와 경쟁하지 않게 함 */
function scheduleDefectDialogIdlePrefetch(fn) {
  console.log('[DEFECT STORE][RGB/IR prefetch DBG] idle 프리패치 예약');
  const run = () => {
    try {
      fn();
    } catch (e) {
      console.warn('[DEFECT STORE] idle RGB/IR prefetch 스케줄 실행 실패:', e);
    }
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 0);
  }
}

/**
 * solarRgbImage 메타(JSON) 이후 CDN 래스터(imgUrl)를 브라우저가 미리 받도록 한다.
 * GET /api/solarRgbImages/{id} 만으로는 풀해상도 파일이 디스크/메모리 캐시에 안 올라갈 수 있음.
 */
function preloadRgbRasterFromMeta(solarRgbImage) {
  const url = solarRgbImage?.imgUrl;
  if (!url || typeof url !== 'string') {
    return;
  }
  const img = new Image();
  img.src = url;
}

/** DrawableImage 의 processedImageUrl 과 동일한 최종 URL로 IR 래스터를 프리로드한다. */
function preloadIrRasterAtProcessedUrl(processedUrl) {
  if (!processedUrl || typeof processedUrl !== 'string') {
    return;
  }
  const img = new Image();
  img.src = processedUrl;
}

/**
 * 결함 모달 네비와 맞춰 대시보드 열화상 목록에서 현재 인덱스·이전·다음 IR 객체를 해석한다.
 */
function resolveDefectDialogThermalNavContext(rootState, currentThermalImage) {
  const images = rootState.dashboard?.thermalImages;
  if (!Array.isArray(images) || images.length === 0) {
    return null;
  }

  const currentTid =
    currentThermalImage?.id != null && currentThermalImage.id !== ''
      ? Number(currentThermalImage.id)
      : null;

  const selectedIdx = rootState.dashboard?.selectedThermalImageIndex ?? -1;
  let index = -1;
  let indexSource = 'none';
  if (
    selectedIdx >= 0 &&
    selectedIdx < images.length &&
    currentTid != null &&
    images[selectedIdx]?.id != null &&
    Number(images[selectedIdx].id) === currentTid
  ) {
    index = selectedIdx;
    indexSource = 'selectedThermalImageIndex';
  } else if (currentTid != null) {
    index = images.findIndex((img) => img && Number(img.id) === currentTid);
    if (index >= 0) {
      indexSource = 'thermalId';
    }
  }
  if (index < 0) {
    index = selectedIdx;
    if (index >= 0) {
      indexSource = 'fallbackSelectedIndex';
    }
  }
  if (index < 0 || index >= images.length) {
    return null;
  }

  return {
    index,
    indexSource,
    images,
    currentThermalImage,
    currentTid,
    prevIr: index > 0 ? images[index - 1] : null,
    nextIr: index < images.length - 1 ? images[index + 1] : null
  };
}

function prefetchAdjacentRgbWithContext(ctx) {
  const currentPairId =
    ctx.currentThermalImage?.pairRgbImageId != null &&
    ctx.currentThermalImage.pairRgbImageId !== ''
      ? Number(ctx.currentThermalImage.pairRgbImageId)
      : null;

  const ids = new Set();
  const pushNeighborPairId = (pairId) => {
    if (pairId == null || pairId === '') {
      return;
    }
    const n = Number(pairId);
    if (!Number.isFinite(n)) {
      return;
    }
    if (currentPairId != null && n === currentPairId) {
      return;
    }
    ids.add(n);
  };

  if (ctx.index > 0) {
    pushNeighborPairId(ctx.images[ctx.index - 1]?.pairRgbImageId);
  }
  if (ctx.index < ctx.images.length - 1) {
    pushNeighborPairId(ctx.images[ctx.index + 1]?.pairRgbImageId);
  }

  console.log('[DEFECT STORE][RGB prefetch DBG] 실행', {
    thermalId: ctx.currentThermalImage?.id,
    currentPairRgbImageId: currentPairId,
    resolvedIndex: ctx.index,
    indexSource: ctx.indexSource,
    listLength: ctx.images.length,
    prevThermalId: ctx.prevIr?.id,
    prevPairRgbImageId: ctx.prevIr?.pairRgbImageId,
    nextThermalId: ctx.nextIr?.id,
    nextPairRgbImageId: ctx.nextIr?.pairRgbImageId,
    prefetchPairRgbIds: [...ids]
  });

  ids.forEach((rgbId) => {
    if (!Number.isFinite(rgbId)) {
      return;
    }
    SolarRgbImageService.getSolarRgbImage(rgbId)
      .then((meta) => {
        preloadRgbRasterFromMeta(meta);
        console.log('[DEFECT STORE][RGB prefetch DBG] 완료 pairRgbImageId=', rgbId);
      })
      .catch(() => {
        console.log('[DEFECT STORE][RGB prefetch DBG] 실패(무시) pairRgbImageId=', rgbId);
      });
  });
}

function prefetchAdjacentIrWithContext(ctx) {
  const currentRaw = ctx.currentThermalImage?.imgUrl;
  const currentProcessed =
    currentRaw && typeof currentRaw === 'string' ? processImageUrlForCdn(currentRaw) : null;

  const urls = new Set();
  const pushNeighborIr = (thermal) => {
    const raw = thermal?.imgUrl;
    if (!raw || typeof raw !== 'string') {
      return;
    }
    const processed = processImageUrlForCdn(raw);
    if (!processed || typeof processed !== 'string') {
      return;
    }
    if (currentProcessed && processed === currentProcessed) {
      return;
    }
    urls.add(processed);
  };

  if (ctx.prevIr) {
    pushNeighborIr(ctx.prevIr);
  }
  if (ctx.nextIr) {
    pushNeighborIr(ctx.nextIr);
  }

  console.log('[DEFECT STORE][IR prefetch DBG] 실행', {
    thermalId: ctx.currentThermalImage?.id,
    resolvedIndex: ctx.index,
    prevThermalId: ctx.prevIr?.id,
    nextThermalId: ctx.nextIr?.id,
    prefetchIrUrls: [...urls]
  });

  urls.forEach((url) => {
    preloadIrRasterAtProcessedUrl(url);
    console.log('[DEFECT STORE][IR prefetch DBG] 래스터 요청', url);
  });
}

/** 인접 IR의 RGB 메타+JPG, IR JPG(DrawableImage 와 동일 CDN 규칙)를 idle 에 프리패치 */
function prefetchAdjacentDefectDialogMedia(rootState, currentThermalImage) {
  if (!rootState.defect?.defectInfoDialog?.open) {
    console.log('[DEFECT STORE][RGB/IR prefetch DBG] 스킵: 결함 모달 닫힘');
    return;
  }

  const ctx = resolveDefectDialogThermalNavContext(rootState, currentThermalImage);
  if (!ctx) {
    console.log('[DEFECT STORE][RGB/IR prefetch DBG] 스킵: thermal 목록 또는 인덱스 없음', {
      thermalId: currentThermalImage?.id
    });
    return;
  }

  prefetchAdjacentRgbWithContext(ctx);
  prefetchAdjacentIrWithContext(ctx);
}

const state = {
  // 결함 정보 다이얼로그 상태
  defectInfoDialog: {
    open: false,
    thermalImage: null,
    solarRgbImage: null,
    isDefectSelected: false,
    showList: false,
    defectTemperature: { min: null, max: null, avr: null },
    defect: getDefaultDefect(),
    // 로컬 state로 관리되던 값들을 추가
    solarThermalImageId: '',
    solarThermalImageName: '',
    valid: false,
    selectedImage: '',
    solarModule: '',
    isEdit: false,
    // 명시적인 defect 모드 플래그 추가
    defectMode: 'none', // 'none', 'new', 'edit_from_map', 'edit_from_list', 'edit_from_module_drawer', 'view'
    /** 결함 목록 열 때 스크롤·포커스할 결함 id (상세에서 목록으로 전환 시 전달) */
    focusDefectListItemId: null,
    /** 좌우 네비: image=리포트 전체 IR, module=같은 모듈 결함, covering=모듈 연필 커버 IR+존 */
    navigationMode: 'image',
    moduleDefectNavList: [],
    moduleDefectNavIndex: 0,
    imageNavList: [],
    imageNavIndex: 0,
    imageNavZoneFilter: 'all',
    showCoveringZoneOverlay: false,
    /** covering 신규 등록 대상 모듈 seq — 모듈리스트 낙관적 생성에 사용 */
    coveringModuleSeq: null
  },

  // 결함 사각형 형태 관리 (전역 상태) - 다른 dialog에서도 유지 단 site가 바뀌면 초기화됨
  defectShapes: createDefaultDefectShapes(),

  // 결함 타입 정보 (고정 데이터) - 공유되는 마스터 데이터
  defectTypes: {
    parentTypes: [],
    loaded: false
  },

  // 지도 갱신 상태
  shouldRefreshMap: false,
  /** requestMapRefresh 시 옵션 메타 (맵 컴포넌트에서 intent와 연결) */
  mapRefreshRequestMeta: null,

  // 결함 목록 갱신 상태
  shouldRenewDefectList: false,

  // 결함 카드 테이블 초기화 상태
  shouldInitDefectCardTable: false,

  // AI 결함 포함 여부
  aiInclude: false,

  // 기본적인 defect 관련 상태만 유지
  defectTemperature: {
    min: null,
    max: null,
    avr: null
  },

  // 결함 카드 테이블 초기화 플래그
  defectCardTableInit: false,

  defaultRects: {},

  defectList: [] // 결함 목록을 전역으로 관리

};

const getters = {
  // 다이얼로그 상태 getters
  defectInfoDialogOpen: state => {
    const isOpen = state.defectInfoDialog.open;
    return isOpen;
  },
  getThermalImage: state => state.defectInfoDialog.thermalImage,
  getDefectTemperature: state => state.defectTemperature,
  defectInfoDialogData: state => state.defectInfoDialog,
  // 결함 사각형 정보 getters
  defectShapesData: state => state.defectShapes,

  // 결함 타입 정보 getters (마스터 데이터)
  defectTypesData: state => state.defectTypes,
  parentDefectTypes: state => state.defectTypes.parentTypes,
  defectTypesLoaded: state => state.defectTypes.loaded,

  // 지도 갱신 관련 getters
  shouldRefreshMap: state => state.shouldRefreshMap,
  mapRefreshRequestMeta: state => state.mapRefreshRequestMeta,
  shouldRenewDefectList: state => state.shouldRenewDefectList,
  shouldInitDefectCardTable: state => state.shouldInitDefectCardTable,

  // AI 결함 포함 여부 getter
  aiInclude: state => state.aiInclude,

  //상세 데이터 관련 getters
  getDefectId: state => state.defectInfoDialog?.defect?.id || null,
  getDefectStatus: state => state.defectInfoDialog?.defect?.defectStatus || null,
  getDefect: state => state.defectInfoDialog?.defect,

  getMainDefectSelectOptions: state => state.defectTypes?.parentTypes || [],

  getSelectedMainDefect: state => state.defectInfoDialog?.defect?.defectTypeInfo?.selected?.selectedMainType || null,
  getSelectedSubDefect: state => state.defectInfoDialog?.defect?.defectTypeInfo?.selected?.selectedSubDefectType || null,
  getShowList: state => state.defectInfoDialog?.showList,
  getIsDefectSelected: state => state.defectInfoDialog?.isDefectSelected,

  // 결함 카드 테이블 초기화 플래그 getter
  getDefectCardTableInit: (state) => state.defectCardTableInit,

  // RGB 이미지 getter
  getRgbImage: (state) => state.defectInfoDialog.solarRgbImage,

  // 로컬 state로 관리되던 값들을 가져오는 getters
  getSolarThermalImageId: (state) => state.defectInfoDialog.solarThermalImageId,
  getSolarThermalImageName: (state) => state.defectInfoDialog.solarThermalImageName,
  getValid: (state) => state.defectInfoDialog.valid,
  getSelectedImage: (state) => state.defectInfoDialog.selectedImage,
  getSolarModule: (state) => state.defectInfoDialog.solarModule,
  getIsEdit: (state) => state.defectInfoDialog.isEdit,

  // defect 모드 getter 추가
  getDefectMode: (state) => state.defectInfoDialog.defectMode,
  getFocusDefectListItemId: (state) => state.defectInfoDialog?.focusDefectListItemId ?? null,
  getDefectNavigationMode: state => state.defectInfoDialog?.navigationMode || 'image',
  isCoveringImageNavMode: state => state.defectInfoDialog?.navigationMode === 'covering',
  getModuleDefectNavList: state => state.defectInfoDialog?.moduleDefectNavList || [],
  getModuleDefectNavIndex: state => state.defectInfoDialog?.moduleDefectNavIndex ?? 0,
  canNavigateModuleDefectPrevious: state => (state.defectInfoDialog?.moduleDefectNavIndex ?? 0) > 0,
  canNavigateModuleDefectNext: state => {
    const list = state.defectInfoDialog?.moduleDefectNavList || [];
    const index = state.defectInfoDialog?.moduleDefectNavIndex ?? 0;
    return list.length > 0 && index < list.length - 1;
  },
  getImageNavList: state => state.defectInfoDialog?.imageNavList || [],
  getImageNavIndex: state => state.defectInfoDialog?.imageNavIndex ?? 0,
  getImageNavZoneFilter: state => state.defectInfoDialog?.imageNavZoneFilter || 'all',
  getCoveringModuleSeq: state => state.defectInfoDialog?.coveringModuleSeq ?? null,
  getShowCoveringZoneOverlay: state =>
    state.defectInfoDialog?.navigationMode === 'covering' &&
    !!state.defectInfoDialog?.showCoveringZoneOverlay,
  getImageNavCurrentZone: state => {
    const list = state.defectInfoDialog?.imageNavList || [];
    const index = state.defectInfoDialog?.imageNavIndex ?? 0;
    return list[index]?.frameZone || null;
  },
  canNavigateImageNavPrevious: state =>
    !!resolveImageNavStep(
      state.defectInfoDialog?.imageNavList || [],
      state.defectInfoDialog?.imageNavIndex ?? 0,
      'previous',
      state.defectInfoDialog?.imageNavZoneFilter || 'all'
    ),
  canNavigateImageNavNext: state =>
    !!resolveImageNavStep(
      state.defectInfoDialog?.imageNavList || [],
      state.defectInfoDialog?.imageNavIndex ?? 0,
      'next',
      state.defectInfoDialog?.imageNavZoneFilter || 'all'
    ),

  // 현재 reportId에 대한 기본 사각형 좌표 조회 getter
  getCurrentReportDefaultRect: (state, getters, rootState) => {
    const reportId = rootState.explorer?.report?.id;
    console.log('[DEFECT STORE] getCurrentReportDefaultRect - reportId:', reportId);
    console.log('[DEFECT STORE] getCurrentReportDefaultRect - state.defectShapes:', state.defectShapes);

    if (reportId && state.defectShapes.reportId === reportId) {
      const rect = {
        x: state.defectShapes.x,
        y: state.defectShapes.y,
        width: state.defectShapes.width,
        height: state.defectShapes.height,
        reportId: state.defectShapes.reportId
      };
      console.log('[DEFECT STORE] 현재 reportId에 대한 기본 사각형 좌표 조회 성공:', rect);
      return rect;
    }
    console.log('[DEFECT STORE] 현재 reportId에 대한 기본 사각형 좌표 없음:', reportId);
    return null;
  },

  getDefectList: state => state.defectList
};

const mutations = {
  // 다이얼로그 관련 mutations
  SET_DEFECT_INFO_DIALOG(
    state,
    {
      open,
      thermalImage,
      solarRgbImage,
      isDefectSelected,
      showList,
      defectTemperature,
      defect,
      isEdit,
      solarThermalImageId,
      solarThermalImageName,
      valid,
      selectedImage,
      solarModule,
      defectMode,
      focusDefectListItemId,
      navigationMode,
      moduleDefectNavList,
      moduleDefectNavIndex,
      imageNavList,
      imageNavIndex,
      imageNavZoneFilter,
      showCoveringZoneOverlay,
      coveringModuleSeq
    }
  ) {
    console.log('[DEFECT STORE] SET_DEFECT_INFO_DIALOG mutation 호출:', {
      open,
      thermalImage,
      isDefectSelected,
      showList,
      defect,
      isEdit,
      defectMode,
      focusDefectListItemId,
      navigationMode
    });

    const oldState = { ...state.defectInfoDialog };
    console.log('[DEFECT STORE] 이전 상태:', oldState);

    state.defectInfoDialog = {
      open,
      thermalImage,
      isDefectSelected,
      showList,
      defectTemperature: defectTemperature || { min: null, max: null, avr: null },
      defect: defect || getDefaultDefect(),
      solarRgbImage: solarRgbImage || null,  // 기존 값 유지
      // 누락된 값들 추가
      isEdit: isEdit !== undefined ? isEdit : false,
      solarThermalImageId: solarThermalImageId || '',
      solarThermalImageName: solarThermalImageName || '',
      valid: valid !== undefined ? valid : false,
      selectedImage: selectedImage || '',
      solarModule: solarModule || '',
      // defect 모드 설정
      defectMode: defectMode || 'none',
      focusDefectListItemId:
        focusDefectListItemId !== undefined
          ? focusDefectListItemId != null && focusDefectListItemId !== ''
            ? Number(focusDefectListItemId)
            : null
          : oldState.focusDefectListItemId ?? null,
      navigationMode:
        navigationMode !== undefined ? navigationMode : oldState.navigationMode || 'image',
      moduleDefectNavList:
        moduleDefectNavList !== undefined ? moduleDefectNavList : oldState.moduleDefectNavList || [],
      moduleDefectNavIndex:
        moduleDefectNavIndex !== undefined
          ? moduleDefectNavIndex
          : oldState.moduleDefectNavIndex ?? 0,
      imageNavList:
        imageNavList !== undefined ? imageNavList : oldState.imageNavList || [],
      imageNavIndex:
        imageNavIndex !== undefined ? imageNavIndex : oldState.imageNavIndex ?? 0,
      imageNavZoneFilter:
        imageNavZoneFilter !== undefined
          ? imageNavZoneFilter
          : oldState.imageNavZoneFilter || 'all',
      showCoveringZoneOverlay:
        showCoveringZoneOverlay !== undefined
          ? !!showCoveringZoneOverlay
          : !!oldState.showCoveringZoneOverlay,
      coveringModuleSeq:
        coveringModuleSeq !== undefined
          ? coveringModuleSeq
          : oldState.coveringModuleSeq ?? null
    };

    console.log('[DEFECT STORE] 새로운 상태:', state.defectInfoDialog);
  },

  SET_SHOW_COVERING_ZONE_OVERLAY(state, visible) {
    state.defectInfoDialog.showCoveringZoneOverlay = !!visible;
  },

  PATCH_CURRENT_COVERING_OVERLAY(state, { moduleOverlayQuad, moduleOverlaySource }) {
    const dialog = state.defectInfoDialog;
    if (!dialog) {
      return;
    }
    if (dialog.thermalImage) {
      Vue.set(dialog.thermalImage, 'moduleOverlayQuad', moduleOverlayQuad);
      Vue.set(dialog.thermalImage, 'moduleOverlaySource', moduleOverlaySource);
    }
    const list = dialog.imageNavList;
    const index = dialog.imageNavIndex ?? 0;
    if (Array.isArray(list) && list[index]) {
      Vue.set(list[index], 'moduleOverlayQuad', moduleOverlayQuad);
      Vue.set(list[index], 'moduleOverlaySource', moduleOverlaySource);
    }
  },

  // showList만 업데이트하는 mutation
  UPDATE_SHOW_LIST(state, showList) {
    console.log('[DEFECT STORE] UPDATE_SHOW_LIST mutation 호출:', showList);
    state.defectInfoDialog.showList = showList;
    console.log('[DEFECT STORE] UPDATE_SHOW_LIST 완료, 현재 showList:', state.defectInfoDialog.showList);
  },

  // RGB 이미지 저장
  SET_RGB_IMAGE(state, rgbImage) {
    console.log('[DEFECT STORE] SET_RGB_IMAGE mutation 호출:', rgbImage);
    state.defectInfoDialog.solarRgbImage = rgbImage;
    console.log('[DEFECT STORE] SET_RGB_IMAGE 완료, 현재 solarRgbImage:', state.defectInfoDialog.solarRgbImage);
  },

  // 결함 타입 상세 정보 저장
  UPDATE_DEFECT_TYPE_INFO(state, defectTypeInfo) {
    console.log('[DEFECT STORE] UPDATE_DEFECT_TYPE_INFO mutation 호출:', defectTypeInfo);
    state.defectInfoDialog.defectTypeInfo = defectTypeInfo;
    console.log('[DEFECT STORE] UPDATE_DEFECT_TYPE_INFO 완료, 현재 defectTypeInfo:', state.defectInfoDialog.defectTypeInfo);
  },

  // 결함 온도 정보 mutations
  SET_DEFECT_TEMPERATURE(state, temperature) {
    state.defectTemperature = temperature;
  },

  // 결함 타입 정보 mutations (마스터 데이터)
  SET_PARENT_DEFECT_TYPES(state, types) {
    const list = Array.isArray(types) ? types : [];
    state.defectTypes.parentTypes = list;
    state.defectTypes.loaded = list.length > 0;
    if (state.defectInfoDialog?.defect?.defectTypeInfo?.options) {
      state.defectInfoDialog.defect.defectTypeInfo.options.mainOptions = list;
    }
  },

  // 지도 갱신 관련 mutations
  SET_SHOULD_REFRESH_MAP(state, should) {
    state.shouldRefreshMap = should;
  },
  SET_MAP_REFRESH_REQUEST_META(state, meta) {
    state.mapRefreshRequestMeta = meta;
  },
  SET_SHOULD_RENEW_DEFECT_LIST(state, should) {
    console.log('[DEFECT STORE] SET_SHOULD_RENEW_DEFECT_LIST mutation 호출:', should);
    state.shouldRenewDefectList = should;
    console.log('[DEFECT STORE] shouldRenewDefectList 상태 변경됨:', state.shouldRenewDefectList);
  },
  SET_SHOULD_INIT_DEFECT_CARD_TABLE(state, should) {
    state.shouldInitDefectCardTable = should;
  },

  // AI 결함 포함 여부 mutation
  SET_AI_INCLUDE(state, include) {
    state.aiInclude = include;
  },

  SET_SELECTED_SUB_DEFECT_TYPE(state, subType) {
    if (state.defectInfoDialog?.defect?.defectTypeInfo?.selected) {
      state.defectInfoDialog.defect.defectTypeInfo.selected.selectedSubDefectType = subType;
    }
  },
  UPDATE_DEFECT_STATUS(state, status) {
    if (state.defectInfoDialog?.defect) {
      state.defectInfoDialog.defect.defectStatus = status;
    }
  },
  UPDATE_DEFECT_DESC(state, desc) {
    if (state.defectInfoDialog?.defect) {
      state.defectInfoDialog.defect.defectDesc = desc;
    }
  },

  SET_SELECTED_MAIN_DEFECT_TYPE(state, mainType) {
    if (state.defectInfoDialog?.defect?.defectTypeInfo?.selected) {
      state.defectInfoDialog.defect.defectTypeInfo.selected.selectedMainType = mainType;
      // 하위 결함 타입도 초기화
      state.defectInfoDialog.defect.defectTypeInfo.selected.selectedSubDefectType = null;
    }
  },
  SET_DEFECT(state, defect) {
    // 서버 데이터를 defectInfoDialog.defect에 저장
    state.defectInfoDialog.defect.id = defect?.id || null;
    state.defectInfoDialog.defect.defectCode = defect?.defectCode || null;
    state.defectInfoDialog.defect.defectStatus = defect?.defectStatus || '';
    state.defectInfoDialog.defect.defectDesc = defect?.defectDesc || '';
    state.defectInfoDialog.defect.defectShapeGeom =
      resolveDefectShapeGeomForOverlay(defect) || undefined;
    state.defectInfoDialog.defect.defectLocation = defect?.defectLocation || undefined;
    state.defectInfoDialog.defect.detectWay = defect?.detectWay || null;
    state.defectInfoDialog.defect.mlDefectConfirmed = defect?.mlDefectConfirmed || null;
    state.defectInfoDialog.defect.maxHeat = defect?.maxHeat || null;
    state.defectInfoDialog.defect.minHeat = defect?.minHeat || null;
    state.defectInfoDialog.defect.averageHeat = defect?.averageHeat || null;
    state.defectInfoDialog.defect.solarModule = defect?.solarModule || null;
    state.defectInfoDialog.defect.solarModuleSeq = defect?.solarModuleSeq ?? null;
  },
  SET_DEFECT_TYPE_INFO(state, defectTypeInfo) {
    state.defectInfoDialog.defect.defectTypeInfo = defectTypeInfo;
  },
  SET_DEFECT_MAIN_TYPE(state, mainType) {
    state.defectInfoDialog.defect.defectTypeInfo.selected.selectedMainType = mainType;
  },
  SET_DEFECT_SUB_TYPE(state, subType) {
    state.defectInfoDialog.defect.defectTypeInfo.selected.selectedSubDefectType = subType;
  },
  // 결함 카드 테이블 초기화 플래그 설정
  SET_DEFECT_CARD_TABLE_INIT(state, init) {
    state.defectCardTableInit = init;
  },
  // 온도 값들을 객체로 한 번에 설정하는 mutation (defect 객체와 분리)
  UPDATE_DEFECT_TEMPERATURE(state, temperatureData) {
    console.log('[RECT_DRAG_TEST] UPDATE_DEFECT_TEMPERATURE mutation 호출됨:', temperatureData);
    console.log('[RECT_DRAG_TEST] mutation 호출 전 defectTemperature 상태:', state.defectTemperature);

    const pick = (v) => (v === null || v === undefined || v === '' ? null : v);
    // defectTemperature 상태만 업데이트 (defect 객체는 변경하지 않음). 0℃ 도 유지 (|| null 금지)
    state.defectTemperature = {
      max: pick(temperatureData.max),
      min: pick(temperatureData.min),
      avr: pick(temperatureData.avr)
    };

    console.log('[RECT_DRAG_TEST] mutation 호출 후 defectTemperature 상태:', state.defectTemperature);
  },
  // 로컬 state로 관리되던 값들을 설정하는 mutations
  UPDATE_SOLAR_THERMAL_IMAGE_ID(state, id) {
    if (state.defectInfoDialog) {
      state.defectInfoDialog.solarThermalImageId = id;
    }
  },
  UPDATE_SOLAR_THERMAL_IMAGE_NAME(state, name) {
    if (state.defectInfoDialog) {
      state.defectInfoDialog.solarThermalImageName = name;
    }
  },
  UPDATE_VALID(state, valid) {
    if (state.defectInfoDialog) {
      state.defectInfoDialog.valid = valid;
    }
  },
  UPDATE_SELECTED_IMAGE(state, image) {
    if (state.defectInfoDialog) {
      state.defectInfoDialog.selectedImage = image;
    }
  },
  UPDATE_SOLAR_MODULE(state, module) {
    if (state.defectInfoDialog) {
      state.defectInfoDialog.solarModule = module;
    }
  },
  UPDATE_IS_EDIT(state, isEdit) {
    console.log('[DEFECT STORE] UPDATE_IS_EDIT mutation 호출:', isEdit);
    state.defectInfoDialog.isEdit = isEdit;
    console.log('[DEFECT STORE] UPDATE_IS_EDIT 완료, 현재 isEdit:', state.defectInfoDialog.isEdit);
  },

  // defect 모드 업데이트 mutation
  UPDATE_DEFECT_MODE(state, mode) {
    console.log('[DEFECT STORE] UPDATE_DEFECT_MODE mutation 호출:', mode);
    state.defectInfoDialog.defectMode = mode;
    console.log('[DEFECT STORE] UPDATE_DEFECT_MODE 완료, 현재 defectMode:', state.defectInfoDialog.defectMode);
  },

  // 기본 사각형 좌표 저장
  SET_DEFAULT_RECT(state, { x, y, width, height, reportId }) {
    state.defectShapes.x = x;
    state.defectShapes.y = y;
    state.defectShapes.width = width;
    state.defectShapes.height = height;
    state.defectShapes.reportId = reportId;
    console.log('[DEFECT STORE] 기본 사각형 좌표 저장:', { x, y, width, height, reportId });
  },

  // 기본 사각형 좌표 초기화
  RESET_DEFAULT_RECT(state) {
    const next = createDefaultDefectShapes();
    state.defectShapes.x = next.x;
    state.defectShapes.y = next.y;
    state.defectShapes.width = next.width;
    state.defectShapes.height = next.height;
    state.defectShapes.reportId = next.reportId;
    console.log('[DEFECT STORE] 기본 사각형 좌표 초기화');
  },

  SET_DEFECT_LIST(state, defectList) {
    state.defectList = defectList;
  }
};

const actions = {
  // 다이얼로그 열기 통합 액션
  async openDefectInfoDialog(
    { dispatch },
    {
      thermalImage,
      defect,
      isDefectSelected,
      showList = false,
      forceReset = false,
      focusDefectListItemId,
      defectMode,
      navigationMode,
      moduleDefectNavList,
      moduleDefectNavIndex,
      imageNavList,
      imageNavIndex,
      imageNavZoneFilter,
      showCoveringZoneOverlay,
      coveringModuleSeq
    }
  ) {
    console.log('[DEFECT STORE] openDefectInfoDialog 시작:', {
      thermalImage,
      defect,
      isDefectSelected,
      showList,
      forceReset,
      focusDefectListItemId,
      defectMode,
      navigationMode
    });
    await dispatch('initializeDefectDialog', {
      dialogOpen: true,
      thermalImage,
      isDefectSelected,
      showList,
      forceReset,
      focusDefectListItemId,
      defectMode,
      navigationMode,
      moduleDefectNavList,
      moduleDefectNavIndex,
      imageNavList,
      imageNavIndex,
      imageNavZoneFilter,
      showCoveringZoneOverlay,
      coveringModuleSeq
    });
    await dispatch('loadDialogData', {
      thermalImage,
      isDefectSelected,
      defect,
      showList
    });
  },

  // 목적별 다이얼로그 열기 액션들
  // 1. 이미지 선택 시 (새 결함 추가 모드)
  openImageForNewDefect(
    { dispatch },
    {
      thermalImage,
      imageNavList = [],
      imageNavIndex = 0,
      navigationMode = 'image',
      coveringModuleSeq = null
    }
  ) {
    console.log('[DEFECT STORE] openImageForNewDefect 시작:', thermalImage);
    return dispatch('openDefectInfoDialog', {
      thermalImage,
      defect: null,
      isDefectSelected: false,
      showList: false,
      forceReset: true,
      defectMode: 'new',
      navigationMode,
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0,
      imageNavList,
      imageNavIndex,
      imageNavZoneFilter: 'all',
      showCoveringZoneOverlay: false,
      coveringModuleSeq
    });
  },

  // 2. 지도에서 결함 선택 시 (기본 정보가 이미 있음)
  openDefectFromMap({ dispatch }, { thermalImage, defect }) {
    console.log('[DEFECT STORE] openDefectFromMap 시작:', { thermalImage, defect });
    dispatch('openDefectInfoDialog', {
      thermalImage,
      defect,
      isDefectSelected: true,
      showList: false,
      forceReset: false,
      defectMode: 'edit_from_map',
      focusDefectListItemId: null,
      navigationMode: 'image',
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0
    });
  },

  // 3. 결함 목록에서 결함 선택 시 (상세 정보를 새로 가져와야 함)
  openDefectFromList({ dispatch }, { thermalImage, defectId, navigationMode = 'image' }) {
    console.log('[DEFECT STORE] openDefectFromList 시작:', { thermalImage, defectId });
    return dispatch('openDefectInfoDialog', {
      thermalImage,
      defect: { id: defectId }, // ID만 가지고 있음
      isDefectSelected: true,
      showList: false,
      forceReset: false,
      defectMode: 'edit_from_list',
      focusDefectListItemId: null,
      navigationMode,
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0
    });
  },

  // 3b. 대시보드 모듈 결함 drawer → 모듈 모드 네비게이션
  openDefectFromModuleDrawer(
    { dispatch },
    { thermalImage, defectId, moduleDefectNavList = [], moduleDefectNavIndex = 0 }
  ) {
    console.log('[DEFECT STORE] openDefectFromModuleDrawer 시작:', {
      thermalImage,
      defectId,
      moduleDefectNavList,
      moduleDefectNavIndex
    });
    return dispatch('openDefectInfoDialog', {
      thermalImage,
      defect: { id: defectId },
      isDefectSelected: true,
      showList: false,
      forceReset: false,
      defectMode: 'edit_from_module_drawer',
      focusDefectListItemId: null,
      navigationMode: 'module',
      moduleDefectNavList,
      moduleDefectNavIndex
    });
  },

  async navigateModuleDefectDialog({ state, dispatch, rootState, commit }, direction) {
    const list = state.defectInfoDialog?.moduleDefectNavList || [];
    const currentIndex = state.defectInfoDialog?.moduleDefectNavIndex ?? 0;
    if (!list.length) {
      return false;
    }

    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    if (newIndex < 0 || newIndex >= list.length) {
      return false;
    }

    const item = list[newIndex];
    const thermalImageId = item?.thermalImgSeq;
    if (!thermalImageId) {
      console.warn('[DEFECT STORE] module nav — thermalImgSeq 없음', item);
      return false;
    }

    const storeAdapter = { state: rootState, dispatch, commit };
    const thermalImage = await resolveThermalImageForDefectDialog(storeAdapter, thermalImageId);
    if (!thermalImage) {
      console.warn('[DEFECT STORE] module nav — 열화상 없음', thermalImageId);
      return false;
    }

    await dispatch('openDefectInfoDialog', {
      thermalImage,
      defect: { id: item.seq },
      isDefectSelected: true,
      showList: false,
      forceReset: false,
      defectMode: 'edit_from_module_drawer',
      navigationMode: 'module',
      moduleDefectNavList: list,
      moduleDefectNavIndex: newIndex
    });
    return true;
  },

  async navigateImageNavDialog({ state, dispatch, rootState, commit }, direction) {
    const list = state.defectInfoDialog?.imageNavList || [];
    const currentIndex = state.defectInfoDialog?.imageNavIndex ?? 0;
    const zoneFilter = state.defectInfoDialog?.imageNavZoneFilter || 'all';
    const navigationMode = state.defectInfoDialog?.navigationMode || 'image';
    const coveringModuleSeq = state.defectInfoDialog?.coveringModuleSeq ?? null;
    const step = resolveImageNavStep(list, currentIndex, direction, zoneFilter);
    if (!step) {
      return false;
    }
    await dispatch('openDefectInfoDialog', {
      thermalImage: step.image,
      defect: null,
      isDefectSelected: false,
      showList: false,
      forceReset: true,
      defectMode: 'new',
      navigationMode,
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0,
      imageNavList: list,
      imageNavIndex: step.index,
      imageNavZoneFilter: zoneFilter,
      coveringModuleSeq
    });
    syncDashboardSelectedThermalImage({ state: rootState, commit, dispatch }, step.image);
    return true;
  },

  async selectImageNavIndex({ state, dispatch, rootState, commit }, index) {
    const list = state.defectInfoDialog?.imageNavList || [];
    const image = list[index];
    if (!image) {
      return false;
    }
    if (Number(index) === Number(state.defectInfoDialog?.imageNavIndex)) {
      return true;
    }
    await dispatch('openDefectInfoDialog', {
      thermalImage: image,
      defect: null,
      isDefectSelected: false,
      showList: false,
      forceReset: true,
      defectMode: 'new',
      navigationMode: state.defectInfoDialog?.navigationMode || 'image',
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0,
      imageNavList: list,
      imageNavIndex: index,
      imageNavZoneFilter: state.defectInfoDialog?.imageNavZoneFilter || 'all',
      coveringModuleSeq: state.defectInfoDialog?.coveringModuleSeq ?? null
    });
    syncDashboardSelectedThermalImage({ state: rootState, commit, dispatch }, image);
    return true;
  },

  async setImageNavZoneFilter({ state, commit, dispatch }, zoneFilter) {
    const next = zoneFilter || 'all';
    commit('SET_IMAGE_NAV_ZONE_FILTER', next);
    const list = state.defectInfoDialog?.imageNavList || [];
    const current = Number(state.defectInfoDialog?.imageNavIndex ?? 0);
    const indexes = coveringIndexesForZoneFilter(list, next);
    if (indexes.length && !indexes.includes(current)) {
      await dispatch('selectImageNavIndex', indexes[0]);
    }
  },

  toggleCoveringZoneOverlay({ state, commit }) {
    commit(
      'SET_SHOW_COVERING_ZONE_OVERLAY',
      !state.defectInfoDialog?.showCoveringZoneOverlay
    );
  },

  // 4. 결함 목록 표시 시
  openDefectList({ dispatch }, { thermalImage, focusDefectId }) {
    console.log('[DEFECT STORE] openDefectList 시작:', { thermalImage, focusDefectId });
    return dispatch('openDefectInfoDialog', {
      thermalImage,
      defect: null,
      isDefectSelected: false,
      showList: true,
      forceReset: true,
      defectMode: 'view',
      focusDefectListItemId:
        focusDefectId != null && focusDefectId !== '' ? Number(focusDefectId) : null,
      navigationMode: 'image',
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0
    });
  },

  // 상태 초기화만 담당 (reset 로직 포함)
  /**
   * dialogOpen: 다이얼로그 열기 여부
   * thermalImage: 열해상도 이미지
   * solarRgbImage: RGB 이미지
   * isDefectSelected: 결함 선택 여부
   * showList: 결함 목록 표시 여부
   * forceReset: 강제 초기화 여부
   */
  initializeDefectDialog(
    { commit, state, dispatch },
    {
      dialogOpen,
      thermalImage,
      solarRgbImage,
      isDefectSelected,
      showList,
      forceReset = false,
      focusDefectListItemId,
      defectMode,
      navigationMode,
      moduleDefectNavList,
      moduleDefectNavIndex,
      imageNavList,
      imageNavIndex,
      imageNavZoneFilter,
      showCoveringZoneOverlay,
      coveringModuleSeq
    }
  ) {
    console.log('[DEFECT STORE] initializeDefectDialog 시작:', {
      dialogOpen,
      thermalImage,
      isDefectSelected,
      showList,
      forceReset,
      focusDefectListItemId,
      defectMode,
      navigationMode
    });
    // 기존 shouldReset 로직 유지
    const shouldReset = forceReset || !state.defectInfoDialog.open;
    console.log('[DEFECT STORE] shouldReset:', shouldReset);

    // thermalImage에서 관련 값들 추출
    const solarThermalImageId = thermalImage?.id || '';
    const solarThermalImageName = thermalImage?.imgName || '';

    const resolvedFocusListItemId = (() => {
      if (focusDefectListItemId !== undefined) {
        return focusDefectListItemId != null && focusDefectListItemId !== ''
          ? Number(focusDefectListItemId)
          : null;
      }
      return shouldReset ? null : state.defectInfoDialog.focusDefectListItemId;
    })();

    const initialState = {
      open: dialogOpen || false,
      thermalImage: thermalImage || null,
      solarRgbImage: shouldReset ? null : solarRgbImage || null,
      isDefectSelected: isDefectSelected || false,
      showList: showList !== undefined ? showList : false,
      defectTemperature: shouldReset ? { min: null, max: null, avr: null } : state.defectInfoDialog.defectTemperature,
      defect: shouldReset ? getDefaultDefect() : state.defectInfoDialog.defect,
      // thermalImage에서 추출한 값들로 설정
      isEdit: shouldReset ? false : state.defectInfoDialog.isEdit,
      solarThermalImageId: shouldReset ? solarThermalImageId : (solarThermalImageId || state.defectInfoDialog.solarThermalImageId),
      solarThermalImageName: shouldReset ? solarThermalImageName : (solarThermalImageName || state.defectInfoDialog.solarThermalImageName),
      valid: shouldReset ? false : state.defectInfoDialog.valid,
      selectedImage: shouldReset ? '' : state.defectInfoDialog.selectedImage,
      solarModule: shouldReset ? '' : state.defectInfoDialog.solarModule,
      // defect 모드 설정
      defectMode:
        defectMode !== undefined
          ? defectMode
          : shouldReset
            ? 'none'
            : state.defectInfoDialog.defectMode,
      focusDefectListItemId: resolvedFocusListItemId,
      navigationMode:
        navigationMode !== undefined
          ? navigationMode
          : shouldReset
            ? 'image'
            : state.defectInfoDialog.navigationMode || 'image',
      moduleDefectNavList:
        moduleDefectNavList !== undefined
          ? moduleDefectNavList
          : shouldReset
            ? []
            : state.defectInfoDialog.moduleDefectNavList || [],
      moduleDefectNavIndex:
        moduleDefectNavIndex !== undefined
          ? moduleDefectNavIndex
          : shouldReset
            ? 0
            : state.defectInfoDialog.moduleDefectNavIndex ?? 0,
      imageNavList:
        imageNavList !== undefined
          ? imageNavList
          : shouldReset
            ? []
            : state.defectInfoDialog.imageNavList || [],
      imageNavIndex:
        imageNavIndex !== undefined
          ? imageNavIndex
          : shouldReset
            ? 0
            : state.defectInfoDialog.imageNavIndex ?? 0,
      imageNavZoneFilter:
        imageNavZoneFilter !== undefined
          ? imageNavZoneFilter
          : shouldReset
            ? 'all'
            : state.defectInfoDialog.imageNavZoneFilter || 'all',
      showCoveringZoneOverlay:
        showCoveringZoneOverlay !== undefined
          ? !!showCoveringZoneOverlay
          : !!state.defectInfoDialog.showCoveringZoneOverlay,
      coveringModuleSeq: (() => {
        if (coveringModuleSeq !== undefined) {
          return coveringModuleSeq == null || coveringModuleSeq === '' ? null : coveringModuleSeq;
        }
        const resolvedNav =
          navigationMode !== undefined
            ? navigationMode
            : shouldReset
              ? 'image'
              : state.defectInfoDialog.navigationMode || 'image';
        if (resolvedNav === 'covering') {
          return state.defectInfoDialog.coveringModuleSeq ?? null;
        }
        return shouldReset ? null : state.defectInfoDialog.coveringModuleSeq ?? null;
      })()
    };

    console.log('[DEFECT STORE] 초기 상태 설정:', initialState);
    commit('SET_DEFECT_INFO_DIALOG', initialState);

    // thermalImage 관련 값들 동기화
    if (thermalImage) {
      dispatch('syncThermalImageData', thermalImage);
    }
  },

  // 2. 데이터 로드 — 접근 모드별 세션 로더가 API·폼 초기화 분기
  async loadDialogData(
    { commit, dispatch, rootState, getters },
    { thermalImage, isDefectSelected, defect, showList }
  ) {
    console.log('[DEFECT STORE] loadDialogData 시작:', {
      thermalImage,
      isDefectSelected,
      defect,
      showList
    });

    if (thermalImage) {
      await dispatch('loadRgbImage', { thermalImage });
    }

    try {
      await loadDefectDialogSession(
        { commit, dispatch, rootState, getters },
        { thermalImage, isDefectSelected, defect, showList }
      );
    } catch (error) {
      console.error('[DEFECT STORE] loadDialogData 실패:', error);
      commit('SET_DEFECT_LIST', []);
      throw error;
    }
  },

  // 다이얼로그 닫기 action
  closeDefectInfoDialog({ commit }) {
    //init해줄것 아래코드는 안됨
    commit('SET_DEFECT_INFO_DIALOG', {
      open: false,
      thermalImage: null,
      solarRgbImage: null,
      isDefectSelected: false,
      showList: false,
      defectTemperature: { min: null, max: null, avr: null },
      defect: getDefaultDefect(),
      // 모든 값들을 초기화
      solarThermalImageId: '',
      solarThermalImageName: '',
      valid: false,
      selectedImage: '',
      solarModule: '',
      isEdit: false,
      // defect 모드 초기화
      defectMode: 'none',
      focusDefectListItemId: null,
      navigationMode: 'image',
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0,
      imageNavList: [],
      imageNavIndex: 0,
      imageNavZoneFilter: 'all',
      showCoveringZoneOverlay: false,
      coveringModuleSeq: null
    });
  },

  // 네비게이션 시 defect dialog 재초기화 (openDefectInfoDialog 패턴 활용)
  async resetDefectDialogForNavigation({ commit, dispatch }, { thermalImage, isDefectSelected = false, showList = false }) {
    console.log('[DEFECT STORE] resetDefectDialogForNavigation 시작:', { thermalImage, isDefectSelected, showList });

    // 1. 상태 초기화 (forceReset = true로 강제 초기화)
    await dispatch('initializeDefectDialog', {
      dialogOpen: true,
      thermalImage,
      isDefectSelected,
      showList,
      forceReset: true,
      navigationMode: 'image',
      moduleDefectNavList: [],
      moduleDefectNavIndex: 0,
      imageNavList: [],
      imageNavIndex: 0,
      imageNavZoneFilter: 'all',
      showCoveringZoneOverlay: false
    });

    commit('UPDATE_DEFECT_MODE', showList ? 'view' : 'new');

    // 2. 새 이미지 결함 목록·폼 데이터 로드
    await dispatch('loadDialogData', {
      thermalImage,
      isDefectSelected,
      defect: null,
      showList
    });
  },

  // showList 업데이트 action
  updateShowList({ commit }, showList) {
    commit('UPDATE_SHOW_LIST', showList);
  },

  // 결함 온도 정보 action
  setDefectTemperature({ commit }, temperature) {
    console.log('[DEFECT STORE] setDefectTemperature action 호출:', temperature);
    commit('SET_DEFECT_TEMPERATURE', temperature);
    console.log('[DEFECT STORE] setDefectTemperature mutation 완료');
  },

  // 결함 타입 정보 로드 (마스터 데이터)
  async loadParentDefectTypes({ commit, getters }) {
    if (getters.defectTypesLoaded) {
      return getters.parentDefectTypes;
    }

    try {
      const types = await fetchParentDefectTypes();
      if (types.length === 0) {
        console.error('[DEFECT STORE] 부모 결함 유형이 비어 있음');
      }
      commit('SET_PARENT_DEFECT_TYPES', types);
      return types;
    } catch (error) {
      console.error('Failed to load parent defect types:', error);
      commit('SET_PARENT_DEFECT_TYPES', []);
      throw error;
    }
  },

  // 지도 갱신 관련 actions
  /**
   * @param {{ correlationId?: number, source?: string, reason?: string }} [payload]
   */
  requestMapRefresh({ commit }, payload = {}) {
    const hasMeta =
      payload &&
      (payload.correlationId != null ||
        payload.source != null ||
        payload.reason != null);
    commit(
      'SET_MAP_REFRESH_REQUEST_META',
      hasMeta
        ? {
            correlationId: payload.correlationId ?? null,
            source: payload.source ?? null,
            reason: payload.reason ?? null
          }
        : null
    );
    commit('SET_SHOULD_REFRESH_MAP', true);
  },
  completeMapRefresh({ commit }) {
    commit('SET_SHOULD_REFRESH_MAP', false);
    commit('SET_MAP_REFRESH_REQUEST_META', null);
  },
  requestDefectListRenew({ commit }) {
    commit('SET_SHOULD_RENEW_DEFECT_LIST', true);
  },
  completeDefectListRenew({ commit }) {
    commit('SET_SHOULD_RENEW_DEFECT_LIST', false);
  },
  requestDefectCardTableInit({ commit }) {
    commit('SET_DEFECT_CARD_TABLE_INIT', true);
    // 1초 후 자동으로 false로 리셋
    setTimeout(() => {
      commit('SET_DEFECT_CARD_TABLE_INIT', false);
    }, 1000);
  },
  completeDefectCardTableInit({ commit }) {
    commit('SET_SHOULD_INIT_DEFECT_CARD_TABLE', false);
  },

  // AI 결함 포함 여부 설정
  setAiInclude({ commit }, include) {
    commit('SET_AI_INCLUDE', include);
  },

  // 결함 데이터 변경 후 전체 갱신 (통합 action)
  /**
   * @param solarThermalImageId 열해상도 이미지 아이디
   * @param reportId 보고서 아이디
   * @param changeType 결함 변경 유형 (create, update, delete)
   */
  async refreshAfterDefectChange(
    { dispatch },
    { solarThermalImageId, reportId, changeType }
  ) {
    console.log('[DEFECT STORE] 결함 변경 후 갱신 시작:', {
      solarThermalImageId,
      reportId,
      changeType
    });

    try {
      const skipFullRefresh = shouldSkipFullDefectRefresh(changeType);

      // 1. 결함 목록 갱신 — 삭제·모듈리스트 생성은 낙관적 반영만, 전체 WFS 재요청 생략
      if (!skipFullRefresh) {
        await dispatch('requestDefectListRenew');
      } else {
        console.log('[DEFECT STORE] ' + changeType + ' — defect list full refresh skipped (optimistic)');
      }

      // 2. 지도 갱신 — 삭제·모듈리스트 생성은 낙관적 반영만, 전체 WFS 재요청 생략
      const correlationId = nextPipelineCorrelationId();
      if (!skipFullRefresh) {
        recordSyncIntent({
          reportId,
          reason: SyncReason.STORE_DEFECT_AFTER_CHANGE,
          source: 'defect.refreshAfterDefectChange',
          extra: { changeType, solarThermalImageId, correlationId }
        });
        await dispatch('requestMapRefresh', {
          correlationId,
          source: 'defect.refreshAfterDefectChange',
          reason: String(changeType ?? '')
        });
      } else {
        console.log('[DEFECT STORE] ' + changeType + ' — map full refresh skipped (optimistic)');
        if (reportId != null && reportId !== '') {
          dispatch('dashboard/fetchDefectSummary', reportId, { root: true });
        }
      }

      // 3. 결함 카드 테이블 초기화 플래그 설정
      await dispatch('requestDefectCardTableInit');

      // defectInfoDialog 초기화
      dispatch('initializeDefectDialog', { dialogOpen: false, thermalImage: null, isDefectSelected: false, showList: false, forceReset: true });
      console.log('[DEFECT STORE] 결함 변경 후 갱신 완료');
    } catch (error) {
      console.error('[DEFECT STORE] 결함 변경 후 갱신 실패:', error);
    }
  },

  /** defectCode null: 상위 유형 CELL 초기값 */
  async setupDefectTypeInfoForCellDefault({ commit, dispatch, rootState }) {
    try {
      await dispatch('loadParentDefectTypes');
      const mainOptions = rootState.defect?.defectTypes?.parentTypes || [];
      const cellType = findCellParentType(mainOptions);
      if (!cellType) {
        console.warn('[DEFECT STORE] CELL 상위 유형을 찾지 못함');
        commit('SET_DEFECT_TYPE_INFO', {
          options: { mainOptions, subOptions: [] },
          selected: { selectedMainType: null, selectedSubDefectType: null }
        });
        return;
      }
      const subOptions = await fetchChildDefectTypes(cellType.id);
      commit('SET_DEFECT_TYPE_INFO', {
        options: { mainOptions, subOptions: subOptions || [] },
        selected: { selectedMainType: cellType, selectedSubDefectType: null }
      });
    } catch (error) {
      console.warn('[DEFECT STORE] CELL 유형 정보 구성 실패:', error);
    }
  },

  // defectTypeInfo 구성 처리
  async setupDefectTypeInfo({ commit, dispatch, rootState }, defectCode) {
    if (!defectCode) {
      console.warn('[DEFECT STORE] defectCode가 없습니다.');
      return;
    }

    try {
      await dispatch('loadParentDefectTypes');
      const mainOptions = rootState.defect?.defectTypes?.parentTypes || [];
      const codeType = getDefectCodeType(defectCode);
      let defectTypeInfo;

      if (codeType === 'single') {
        defectTypeInfo = {
          options: {
            mainOptions,
            subOptions: []
          },
          selected: {
            selectedMainType: defectCode,
            selectedSubDefectType: null
          }
        };
        commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
      } else if (codeType === 'main') {
        defectTypeInfo = {
          options: {
            mainOptions,
            subOptions: defectCode.children || []
          },
          selected: {
            selectedMainType: defectCode,
            selectedSubDefectType: null
          }
        };
        commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
      } else if (codeType === 'detail') {
        // API 호출하여 하위 타입들 가져오기
        try {
          console.log('[DEFECT STORE] 하위 결함 타입 로드 시작:',defectCode.parent,  defectCode.parent.id);
          const childTypes = await fetchChildDefectTypes(defectCode.parent.id);
          defectTypeInfo = {
            options: {
              mainOptions,
              subOptions: childTypes || []
            },
            selected: {
              selectedMainType: defectCode.parent,
              selectedSubDefectType: defectCode
            }
          };
          commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
        } catch (error) {
          console.warn('[DEFECT STORE] 하위 결함 타입 로드 실패:', error);
          // API 호출 실패 시 기본값 설정
          defectTypeInfo = {
            options: {
              mainOptions,
              subOptions: []
            },
            selected: {
              selectedMainType: defectCode.parent,
              selectedSubDefectType: defectCode
            }
          };
          commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
        }
      } else {
        console.warn('defectCode에 parent/children 정보가 없습니다:', defectCode);
        // 기본값 설정
        defectTypeInfo = {
          options: {
            mainOptions,
            subOptions: []
          },
          selected: {
            selectedMainType: null,
            selectedSubDefectType: null
          }
        };
        commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
      }
    } catch (error) {
      console.warn('[DEFECT STORE] 결함 타입 정보 구성 실패:', error);
      // 오류 발생 시 기본값 설정
      const mainOptions = rootState.defect?.defectTypes?.parentTypes || [];
      const defectTypeInfo = {
        options: {
          mainOptions,
          subOptions: []
        },
        selected: {
          selectedMainType: null,
          selectedSubDefectType: null
        }
      };
      commit('SET_DEFECT_TYPE_INFO', defectTypeInfo);
    }
  },

  // RGB 이미지 로드 액션
  async loadRgbImage({ commit, rootState }, { thermalImage }) {
    console.log('[DEFECT STORE] loadRgbImage 시작:', thermalImage);

    if (!thermalImage?.pairRgbImageId) {
      console.log('[DEFECT STORE] pairRgbImageId가 없어서 RGB 이미지 로드 건너뜀');
      commit('SET_RGB_IMAGE', null);
      return null;
    }

    const pairId = thermalImage.pairRgbImageId;

    try {
      const rgbImage = await SolarRgbImageService.getSolarRgbImage(pairId);
      commit('SET_RGB_IMAGE', rgbImage);
      console.log('[DEFECT STORE] RGB 이미지 로드 완료:', rgbImage);
      if (!rootState.defect?.defectInfoDialog?.open) {
        return rgbImage;
      }
      scheduleDefectDialogIdlePrefetch(() => {
        prefetchAdjacentDefectDialogMedia(rootState, thermalImage);
      });
      return rgbImage;
    } catch (error) {
      console.error('[DEFECT STORE] RGB 이미지 로드 실패:', error);
      commit('SET_RGB_IMAGE', null);
      throw error;
    }
  },

  // DefectInfoCard 상태 초기화 action
  resetDefectInfoCard({ commit }) {
    console.log('[DEFECT STORE] resetDefectInfoCard action 호출');
    commit('UPDATE_VALID', false);
    commit('UPDATE_SELECTED_IMAGE', '');
    commit('UPDATE_SOLAR_MODULE', '');
    commit('UPDATE_SOLAR_THERMAL_IMAGE_ID', '');
    commit('UPDATE_SOLAR_THERMAL_IMAGE_NAME', '');
    commit('UPDATE_IS_EDIT', false);
    console.log('[DEFECT STORE] resetDefectInfoCard 완료');
  },

  /**
   * thermalImage 변경 시 관련 값들을 자동으로 동기화
   * @param {Object} thermalImage - 열화상 이미지 객체
   */
  syncThermalImageData({ commit }, thermalImage) {
    console.log('[DEFECT STORE] syncThermalImageData 시작:', thermalImage);

    if (!thermalImage) {
      console.log('[DEFECT STORE] thermalImage가 없어서 동기화 건너뜀');
      return;
    }

    // defect store의 값들 동기화
    if (thermalImage.id) {
      commit('UPDATE_SOLAR_THERMAL_IMAGE_ID', thermalImage.id);
    }
    if (thermalImage.imgName) {
      commit('UPDATE_SOLAR_THERMAL_IMAGE_NAME', thermalImage.imgName);
    }

    console.log('[DEFECT STORE] syncThermalImageData 완료');
  },

  // 기본 사각형 좌표 저장 액션
  saveDefaultRect({ commit, rootState }, { x, y, width, height }) {

    const reportId = rootState.explorer?.report?.id;
    console.log('[DEFECT STORE] reportId:', reportId);

    if (reportId) {
      commit('SET_DEFAULT_RECT', { x, y, width, height, reportId });
      console.log('[DEFECT STORE] === saveDefaultRect 액션 완료 ===');
    } else {
      console.warn('[DEFECT STORE] ❌ reportId가 없어서 저장 실패');
    }
  },

  // reportId 변경 시 기본 사각형 좌표 초기화
  resetDefectRect({ commit, rootState }) {
    console.log('[DEFECT STORE] Report ID 변경 감지, 사각형 초기화');
    const currentReportId = rootState.explorer?.report?.id;
    console.log('[DEFECT STORE] 현재 Report ID:', currentReportId);

    // 현재 저장된 사각형의 reportId와 비교
    if (this.state.defectShapes.reportId && this.state.defectShapes.reportId !== currentReportId) {
      console.log('[DEFECT STORE] Report ID가 변경됨, 사각형 초기화 실행');
      commit('RESET_DEFAULT_RECT');
    } else {
      console.log('[DEFECT STORE] Report ID가 동일하거나 초기 상태, 사각형 초기화 건너뜀');
    }
  }
};

function getDefaultDefect() {
  return {
    id: null,
    detectWay: null,
    defectStatus: 'FOUND', // 초기값을 "FOUND"로 설정
    defectDesc: '',
    solarThermalImage: '',
    solarExamineReport: '',
    defectShapeGeom: undefined,
    defectLocation: undefined,
    defectCode: null,
    mlDefectConfirmed: null,
    maxHeat: null,
    minHeat: null,
    averageHeat: null,
    defectTypeInfo: {
      options: {
        mainOptions: [],
        subOptions: []
      },
      selected: {
        selectedMainType: null,
        selectedSubDefectType: null
      }
    }
  };
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
